<template>
  <SfModalPortal
    class="sf-modal-persistent"
    :align="modalAlign.right"
    :animated="true"
    :close-on-backdrop-click="false"
    :close-on-escape="false"
    :footer-padding="0"
    :hide-close="true"
    :eager="eager"
    :modal-id="props.modalId"
    :scrollable-content="scrollableContent"
    :size="size"
    :variant="modalVariants.docked"
    @opened="handleOpen"
    @close="handleClose"
    @close-attempt="handleCloseAttempt"
  >
    <template #title>
      <slot name="title" />
    </template>
    <template #messages>
      <slot name="messages" />
    </template>
    <template #body>
      <slot name="body" />
    </template>
    <template #messages-bottom>
      <slot name="messages-bottom" />
    </template>
    <template #footer="{ closeAfterAnimation }">
      <div
        class="
          tw-flex
          tw-justify-between
          tw-w-full
          tw-px-6
          tw-py-[18px]
          tw-border
          tw-border-solid
          tw-border-x-0
          tw-border-t
          tw-border-b-0
          tw-border-sf-neutral-lighter-2
        "
      >
        <div class="tw-flex tw-items-center">
          <div
            v-tooltip:[tooltipModifier]="cancelTooltip"
            :class="closeButtonClasses"
          >
            <SfButton
              variant="default"
              @click="closeAfterAnimation(handleClose)"
            >
              <span class="tw-inline-flex tw-items-center tw-gap-0.5">
                <span class="icon icon-close" /><span>{{ cancelLabel }}</span>
              </span>
            </SfButton>
          </div>
        </div>

        <div>
          <slot name="footer-right" />
        </div>
      </div>
    </template>
  </SfModalPortal>
</template>
<script setup lang="ts" generic="ID extends ModalPortalIdentifier">
import type { ModalSize } from './constants'
import type { ModalPortalIdentifier, SilverfinEvents } from '@app/types/index'

import { computed, ref, onBeforeUnmount } from 'vue'

import SfModalPortal from './sf-modal-portal.vue'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import { modalAlign, modalSizes, modalVariants } from './constants'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'SfModalPersistent',
})

interface Props {
  cancelLabel: string
  cancelTooltip?: string
  eager?: boolean
  modalId: ID
  scrollableContent?: boolean
  size?: ModalSize
}

const props = withDefaults(defineProps<Props>(), {
  cancelTooltip: '',
  size: modalSizes.xsm,
})

let clearBackdropClickTimeout: ReturnType<typeof setTimeout> | null = null
let clearTooltipTimeout: ReturnType<typeof setTimeout> | null = null

const forceTooltip = ref(false)
const tooltipModifier = computed(() => {
  return { force: forceTooltip.value }
})

const hasClickedOutside = ref(false)

const closeButtonClasses = computed(() => {
  return {
    'tw-animate-shake': hasClickedOutside.value,
  }
})

function handleCloseAttempt() {
  if (clearBackdropClickTimeout) {
    clearTimeout(clearBackdropClickTimeout)
  }

  if (clearTooltipTimeout) {
    clearTimeout(clearTooltipTimeout)
  }

  hasClickedOutside.value = true
  forceTooltip.value = false

  clearBackdropClickTimeout = setTimeout(() => {
    hasClickedOutside.value = false
    forceTooltip.value = true
  }, 250)

  clearTooltipTimeout = setTimeout(() => {
    forceTooltip.value = false
  }, 2000)
}

onBeforeUnmount(() => {
  if (clearBackdropClickTimeout) {
    clearTimeout(clearBackdropClickTimeout)
  }

  if (clearTooltipTimeout) {
    clearTimeout(clearTooltipTimeout)
  }
})

const emit =
  defineEmits<{
    close: []
    opened: [SilverfinEvents[`${ID}-opened`]]
  }>()

const handleOpen = (payload: SilverfinEvents[`${ID}-opened`]) => {
  emit('opened', payload)
}

const handleClose = () => {
  emit('close')
  window.modalCitizen.closeModalPortal()
}
</script>
<style lang="scss">
.sf-modal-persistent {
  --modal-body-margin: 0;
}
</style>
